import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Clock,
  DollarSign,
  TrendingDown,
  Users,
  Brain,
  Calculator,
  Zap,
  Target,
  CheckCircle,
  XCircle,
  BarChart3,
  Rocket,
  ArrowRight,
  Lightbulb,
  ShieldAlert,
  TrendingUp,
  Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOHead from "@/components/SEOHead";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogFooter from "@/components/blog/BlogFooter";
import BlogPostHeader from "@/components/blog/BlogPostHeader";
import BlogCTA from "@/components/blog/BlogCTA";
import heroCover from "@/assets/blog/hero-custo-nao-automatizar.jpg";
import imgEquipe from "@/assets/blog/custo-automatizar/equipe-trabalhando.jpg";
import imgDashboard from "@/assets/blog/custo-automatizar/dashboard-dados.jpg";
import imgCalculadora from "@/assets/blog/custo-automatizar/calculadora-financas.jpg";
import imgRobo from "@/assets/blog/custo-automatizar/robo-automatizacao.jpg";

const BlogPostCustoNaoAutomatizar = () => {
  return (
    <>
      <SEOHead
        title="Quanto Custa Não Automatizar Sua Empresa em 2026? Cálculo Completo | Magnet Link"
        description="Descubra o custo real de operar sem automação: horas perdidas, leads que escapam, erros humanos e oportunidades de receita. Calculadora prática, exemplos reais e o caminho para automatizar com IA."
        keywords="custo de não automatizar, automação empresarial, automação de marketing, automação de vendas, ROI de automação, calculadora de automação, IA empresarial, qualificação automática de leads, formulários inteligentes"
        canonicalUrl="/blog/quanto-custa-nao-automatizar-sua-empresa"
        ogType="article"
        articlePublishedTime="2026-04-13T10:00:00Z"
        articleAuthor="Equipe Magnet Link"
      />

      <div className="min-h-screen bg-background">
        <BlogHeader />

        <BlogPostHeader
          title="Quanto Custa Não Automatizar"
          subtitle="Sua Empresa em 2026?"
          category="Automação & Produtividade"
          categoryColor="primary"
          date="2026-04-13"
          dateFormatted="13 de Abril de 2026"
          readTime="32 minutos de leitura"
          coverImage={heroCover}
          coverImageAlt="Ilustração 3D de um ímã branco atraindo moedas de ouro, engrenagens e um relógio, simbolizando o custo do tempo e dinheiro perdidos sem automação"
          breadcrumbLabel="Quanto Custa Não Automatizar"
        />

        <main role="main" aria-label="Conteúdo do artigo">
          <article className="py-12 md:py-16">
            <div className="container mx-auto px-4">
              <div className="mx-auto max-w-4xl">

                {/* Introduction */}
                <p className="text-lg text-muted-foreground md:text-xl leading-relaxed mb-8">
                  Existe uma pergunta que quase nenhum empresário brasileiro faz, mas que pode ser a mais cara
                  da história do seu negócio: <strong className="text-foreground">quanto custa, mês após mês,
                  continuar fazendo manualmente aquilo que já poderia ser automatizado?</strong> A maioria olha
                  para o preço de uma ferramenta de automação e pensa "está caro". Quase ninguém calcula o custo
                  invisível de não a contratar — e esse custo, em 2026, é maior do que jamais foi.
                </p>

                <p className="text-lg text-muted-foreground leading-relaxed mb-12">
                  Neste guia, vamos desmontar peça por peça o verdadeiro preço da operação manual: horas humanas
                  desperdiçadas, leads que esfriam antes do contato, erros de digitação que viram multas, oportunidades
                  perdidas para concorrentes mais rápidos. Mais do que números, você vai sair com uma calculadora
                  mental — e prática — para olhar para sua própria empresa e descobrir quanto está deixando na
                  mesa <em>agora mesmo</em>.
                </p>

                {/* Stats banner */}
                <div className="mb-16 grid gap-4 md:grid-cols-3" role="region" aria-label="Estatísticas de automação">
                  <Card className="border-primary/30 bg-primary/5">
                    <CardContent className="p-6 text-center">
                      <DollarSign className="mx-auto mb-2 h-8 w-8 text-primary" aria-hidden="true" />
                      <p className="text-3xl font-bold">R$ 78 mil</p>
                      <p className="text-sm text-muted-foreground">perdidos por ano pela pequena empresa média sem automação</p>
                    </CardContent>
                  </Card>
                  <Card className="border-accent/30 bg-accent/5">
                    <CardContent className="p-6 text-center">
                      <Clock className="mx-auto mb-2 h-8 w-8 text-accent" aria-hidden="true" />
                      <p className="text-3xl font-bold">21 h</p>
                      <p className="text-sm text-muted-foreground">por semana gastas em tarefas que IA já resolve</p>
                    </CardContent>
                  </Card>
                  <Card className="border-destructive/30 bg-destructive/5">
                    <CardContent className="p-6 text-center">
                      <TrendingDown className="mx-auto mb-2 h-8 w-8 text-destructive" aria-hidden="true" />
                      <p className="text-3xl font-bold">73%</p>
                      <p className="text-sm text-muted-foreground">dos leads esfriam por demora superior a 1 hora no primeiro contato</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Chapter 1 */}
                <section className="mb-16" aria-labelledby="chapter-1">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10" aria-hidden="true">
                      <AlertTriangle className="h-5 w-5 text-primary" />
                    </div>
                    <h2 id="chapter-1" className="text-2xl font-bold md:text-3xl">
                      1. O Mito da "Economia" em Não Automatizar
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    A primeira distorção mental que precisamos quebrar é a ideia de que "não pagar por uma
                    ferramenta" significa "economizar dinheiro". No mundo industrial do século XX, comprar uma
                    máquina era um investimento de capital pesado, com payback de anos. Em 2026, automação é
                    software vendido por mensalidade — e o equivalente humano daquela "máquina" custa, em
                    salário, encargos, treinamento e rotatividade, dezenas de vezes mais por mês do que a
                    ferramenta que a substituiria.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Quando um empresário diz "não temos orçamento para automatizar", o que ele realmente está
                    dizendo é: "preferimos continuar pagando o custo escondido". Esse custo escondido tem nome,
                    sobrenome e CPF — está distribuído entre as pessoas da equipe que perdem horas copiando
                    dados de um sistema para outro, respondendo às mesmas perguntas pela centésima vez, gerando
                    propostas manualmente e correndo atrás de informações que poderiam estar em um dashboard.
                  </p>

                  <figure className="mb-6">
                    <img
                      src={imgEquipe}
                      alt="Equipe de profissionais brasileiros trabalhando em escritório com computadores e papéis, ilustrando rotina manual sem automação"
                      className="rounded-xl w-full object-cover"
                      loading="lazy"
                    />
                    <figcaption className="text-sm text-muted-foreground mt-2 text-center italic">
                      O trabalho manual repetitivo é o imposto invisível que sua operação paga todos os meses.
                    </figcaption>
                  </figure>

                  <div className="rounded-xl border border-border bg-muted/30 p-6 mb-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-accent" aria-hidden="true" />
                      A pergunta que muda a perspectiva
                    </h3>
                    <p className="text-muted-foreground">
                      Em vez de perguntar "quanto custa essa ferramenta?", pergunte: <strong className="text-foreground">
                      "quantas horas humanas, por mês, ela vai me devolver?"</strong> Se a resposta for maior do que
                      o salário-hora da pessoa que faria essa tarefa, multiplicado pelas horas economizadas, você já
                      está no positivo no primeiro mês.
                    </p>
                  </div>
                </section>

                {/* Chapter 2 — Calculator-style interactive section */}
                <section className="mb-16" aria-labelledby="chapter-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10" aria-hidden="true">
                      <Calculator className="h-5 w-5 text-accent" />
                    </div>
                    <h2 id="chapter-2" className="text-2xl font-bold md:text-3xl">
                      2. A Calculadora do Prejuízo Invisível
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Vamos ao concreto. Pegue papel e caneta — ou abra uma planilha. A fórmula a seguir é simples
                    o suficiente para qualquer empresário aplicar em cinco minutos, e poderosa o suficiente para
                    mudar como você enxerga sua operação.
                  </p>

                  <div className="mb-8 rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/5 to-accent/5 p-6 md:p-8">
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                      <Calculator className="h-6 w-6 text-primary" aria-hidden="true" />
                      Fórmula do Custo Oculto Mensal
                    </h3>

                    <div className="space-y-4">
                      <div className="flex items-start gap-3 rounded-lg bg-background/60 p-4">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">1</span>
                        <div>
                          <p className="font-semibold">Horas semanais gastas em tarefas repetitivas</p>
                          <p className="text-sm text-muted-foreground">Some o tempo da equipe inteira: copiar e colar dados, gerar relatórios, responder perguntas idênticas, qualificar leads na unha.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 rounded-lg bg-background/60 p-4">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">2</span>
                        <div>
                          <p className="font-semibold">Multiplique por 4,3 (semanas no mês) e pelo custo-hora médio</p>
                          <p className="text-sm text-muted-foreground">Custo-hora = salário ÷ 160. Lembre de incluir encargos: multiplique o salário por 1,8 antes de dividir.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 rounded-lg bg-background/60 p-4">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">3</span>
                        <div>
                          <p className="font-semibold">Some o lucro perdido por leads não atendidos</p>
                          <p className="text-sm text-muted-foreground">Estime quantos leads por mês ficam sem resposta em até 1 h. Multiplique por sua taxa de conversão e ticket médio.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 rounded-lg bg-background/60 p-4">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-foreground">=</span>
                        <div>
                          <p className="font-semibold text-foreground">Seu custo mensal de NÃO automatizar</p>
                          <p className="text-sm text-muted-foreground">Compare com qualquer ferramenta de automação de marketing, vendas ou IA. O número costuma assustar.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Exemplo prático: uma empresa de serviços com 4 funcionários administrativos, salário médio
                    de R$ 4.500, perdendo 5 horas semanais cada um em tarefas automatizáveis. Custo-hora com
                    encargos: R$ 50,60. Custo mensal só de operação manual: <strong className="text-foreground">R$ 4.351</strong>.
                    Some a isso 30 leads/mês perdidos por demora de resposta com ticket médio de R$ 1.800 e
                    conversão de 8%: mais <strong className="text-foreground">R$ 4.320</strong>. Total real:
                    perto de <strong className="text-primary">R$ 8.671 por mês — R$ 104 mil por ano</strong>.
                  </p>

                  <figure className="mb-6">
                    <img
                      src={imgCalculadora}
                      alt="Calculadora ao lado de notas de dinheiro e gráfico financeiro, simbolizando o cálculo do custo oculto da operação manual"
                      className="rounded-xl w-full object-cover"
                      loading="lazy"
                    />
                  </figure>
                </section>

                {/* CTA at chapter 3 */}
                <section className="mb-16" aria-labelledby="chapter-3">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10" aria-hidden="true">
                      <Clock className="h-5 w-5 text-primary" />
                    </div>
                    <h2 id="chapter-3" className="text-2xl font-bold md:text-3xl">
                      3. O Custo do Tempo: Sua Equipe Não Trabalha 8 Horas por Dia
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Pesquisas da McKinsey e Asana publicadas em 2025 mostram um número desconfortável: o
                    profissional brasileiro de escritório gasta, em média, <strong className="text-foreground">58%
                    do dia em "trabalho sobre o trabalho"</strong> — buscar informação, mudar de aba, replicar
                    dados, alinhar status, formatar planilha. Isso significa que, de 8 horas pagas, apenas 3,3
                    são dedicadas à atividade que efetivamente gera valor.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Quando você instala automação para as tarefas repetitivas — formulários inteligentes que
                    qualificam leads sozinhos, IA que gera o PDF, integrações que jogam o dado direto no CRM —
                    você não está apenas economizando horas. Você está <em>devolvendo</em> à sua equipe a
                    capacidade de fazer o trabalho que justifica a contratação dela: pensar, atender bem o
                    cliente, fechar negócios complexos.
                  </p>

                  <div className="mb-8 rounded-xl border border-border overflow-hidden">
                    <div className="grid md:grid-cols-2">
                      <div className="bg-destructive/5 p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <XCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
                          <h3 className="font-bold">Sem automação</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li>• 3 h preenchendo planilhas manualmente</li>
                          <li>• 2 h triando leads que não fecham</li>
                          <li>• 1 h respondendo dúvidas repetidas</li>
                          <li>• 1,5 h gerando propostas no Word</li>
                          <li>• 0,5 h efetivamente vendendo</li>
                        </ul>
                      </div>
                      <div className="bg-primary/5 p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="h-5 w-5 text-primary" aria-hidden="true" />
                          <h3 className="font-bold">Com automação + IA</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground">
                          <li>• 0 h — dado entra direto pelo formulário</li>
                          <li>• 0 h — IA qualifica e descarta antes de chegar</li>
                          <li>• 0,5 h — chatbot resolve FAQ sozinho</li>
                          <li>• 0,2 h — proposta gerada em PDF automático</li>
                          <li>• 6,3 h vendendo e fechando contratos</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Inline CTA at the third section, as requested */}
                  <BlogCTA
                    title="Pare de Pagar o Imposto do Manual"
                    description="Crie agora seu primeiro formulário inteligente com IA, gere PDFs automáticos e devolva horas reais para a sua equipe. Comece de graça em menos de 2 minutos."
                    buttonText="Automatizar Meu Negócio Agora"
                    buttonLink="/auth?mode=signup"
                  />
                </section>

                {/* Chapter 4 */}
                <section className="mb-16" aria-labelledby="chapter-4">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10" aria-hidden="true">
                      <Users className="h-5 w-5 text-accent" />
                    </div>
                    <h2 id="chapter-4" className="text-2xl font-bold md:text-3xl">
                      4. O Custo dos Leads Que Você Já Pagou e Perdeu
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Esse é o custo mais doloroso porque é duplo: você gastou para atrair o lead — tráfego pago,
                    SEO, evento, indicação — e depois o perdeu por não conseguir responder a tempo. Estudo
                    clássico do MIT, replicado em 2024 pela RD Station no mercado brasileiro, mostra que leads
                    contactados em <strong className="text-foreground">menos de 5 minutos têm conversão até 9
                    vezes maior</strong> do que leads contactados em mais de 1 hora.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Operação manual praticamente impossibilita essa janela de 5 minutos. O lead preenche um
                    formulário às 22h de uma sexta-feira; alguém só vai abrir o e-mail na segunda às 9h. Nesse
                    intervalo, o lead já visitou o site de três concorrentes, recebeu uma ligação automática de
                    um deles, e talvez já tenha decidido. Você pagou pelo clique, mas quem fechou foi outro.
                  </p>

                  <div className="mb-8 grid gap-4 md:grid-cols-2">
                    <Card className="border-border">
                      <CardContent className="p-6">
                        <ShieldAlert className="h-8 w-8 text-destructive mb-3" aria-hidden="true" />
                        <h3 className="font-bold mb-2">CAC inflado</h3>
                        <p className="text-sm text-muted-foreground">
                          Se metade dos leads pagos esfria antes do contato, seu Custo de Aquisição de Cliente
                          dobra silenciosamente. Sua planilha mostra um CAC de R$ 80; o real é R$ 160.
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="p-6">
                        <TrendingDown className="h-8 w-8 text-destructive mb-3" aria-hidden="true" />
                        <h3 className="font-bold mb-2">LTV reduzido</h3>
                        <p className="text-sm text-muted-foreground">
                          Cliente mal qualificado entra errado, paga pouco, reclama muito e cancela cedo.
                          Automação na entrada blinda o LTV no fundo do funil.
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Automatizar a entrada não é luxo de empresa grande — é defesa de margem para empresa
                    pequena. Um formulário inteligente que qualifica, dispara WhatsApp automático e entrega um
                    PDF personalizado em segundos faz, em 22h de uma sexta, o que um SDR humano não faria nem
                    na segunda de manhã.
                  </p>
                </section>

                {/* Chapter 5 — Real cases */}
                <section className="mb-16" aria-labelledby="chapter-5">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10" aria-hidden="true">
                      <BarChart3 className="h-5 w-5 text-primary" />
                    </div>
                    <h2 id="chapter-5" className="text-2xl font-bold md:text-3xl">
                      5. Três Cenários Reais — e a Conta Que Eles Não Faziam
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Para tornar isso palpável, vamos a três cenários reconstruídos a partir de empresas reais
                    (com nomes e setores ajustados). Em cada um, você verá o "custo invisível" calculado
                    explicitamente — e o quanto a automação devolveu no primeiro trimestre.
                  </p>

                  <div className="space-y-6 mb-8">
                    <Card className="border-l-4 border-l-primary">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="h-5 w-5 text-primary" aria-hidden="true" />
                          <h3 className="font-bold">Caso 1 — Clínica odontológica de médio porte</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          5 recepcionistas qualificavam leads por WhatsApp, agendavam manualmente, enviavam orçamento
                          em PDF feito no Word. Tempo médio de resposta: 4 horas.
                        </p>
                        <p className="text-sm">
                          <strong>Custo invisível mensal:</strong> R$ 12.400 entre horas humanas e leads perdidos.<br />
                          <strong>Solução:</strong> formulário inteligente + agendamento automático + PDF gerado por IA.<br />
                          <strong>Resultado em 90 dias:</strong> +38% de orçamentos enviados, +22% de fechamento, recepção liberada para acolhimento real.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-accent">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <Brain className="h-5 w-5 text-accent" aria-hidden="true" />
                          <h3 className="font-bold">Caso 2 — Consultoria financeira B2B</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          O sócio principal qualificava cada lead em uma call de 30 min antes de aceitar a proposta.
                          70% das calls eram com leads fora do perfil.
                        </p>
                        <p className="text-sm">
                          <strong>Custo invisível mensal:</strong> R$ 18.000 só do tempo do sócio em calls inúteis.<br />
                          <strong>Solução:</strong> diagnóstico digital com IA gerando PDF de pré-análise; só leads aprovados marcam call.<br />
                          <strong>Resultado em 90 dias:</strong> calls reduzidas em 65%, taxa de fechamento por call subiu de 11% para 41%.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-primary">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <Rocket className="h-5 w-5 text-primary" aria-hidden="true" />
                          <h3 className="font-bold">Caso 3 — Agência de imigração</h3>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          Cada novo lead recebia o mesmo questionário em Word, retornado por e-mail, copiado para uma
                          planilha, analisado em até 5 dias. Conversão: 6%.
                        </p>
                        <p className="text-sm">
                          <strong>Custo invisível mensal:</strong> R$ 9.700 em horas humanas + leads que iam para concorrentes durante os 5 dias.<br />
                          <strong>Solução:</strong> formulário com IA gerando análise inicial em PDF na hora.<br />
                          <strong>Resultado em 90 dias:</strong> conversão saltou para 19%, ticket médio aumentou 27% pela percepção de profissionalismo.
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <figure className="mb-6">
                    <img
                      src={imgDashboard}
                      alt="Dashboard analítico em tela de notebook mostrando gráficos de crescimento e métricas de negócio"
                      className="rounded-xl w-full object-cover"
                      loading="lazy"
                    />
                    <figcaption className="text-sm text-muted-foreground mt-2 text-center italic">
                      Quando os dados entram automaticamente, decisões deixam de depender de "achismo".
                    </figcaption>
                  </figure>
                </section>

                {/* Chapter 6 */}
                <section className="mb-16" aria-labelledby="chapter-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10" aria-hidden="true">
                      <ShieldAlert className="h-5 w-5 text-accent" />
                    </div>
                    <h2 id="chapter-6" className="text-2xl font-bold md:text-3xl">
                      6. O Custo dos Erros Humanos: Pequenos Vazamentos, Grande Prejuízo
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Não é sobre culpar a equipe. É sobre reconhecer que humanos cometem entre 1% e 4% de erros
                    em tarefas repetitivas de digitação, segundo estudos clássicos de fatores humanos. Em uma
                    operação que processa 500 cadastros por mês, isso significa entre 5 e 20 cadastros com
                    erro: e-mail trocado, telefone errado, valor digitado a mais, NF emitida com CNPJ errado.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Cada erro tem cauda longa: cobrança devolvida, retrabalho, multa fiscal, cliente irritado,
                    NPS prejudicado, processo no PROCON. Não é raro que o custo médio de um único erro de
                    cadastro ultrapasse R$ 250 quando se contabilizam todas as horas envolvidas na correção.
                    Vinte erros por mês: R$ 5.000 evitáveis, todo mês, para sempre, enquanto a operação for manual.
                  </p>

                  <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 mb-6">
                    <h3 className="font-bold mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
                      Os 4 vazamentos mais comuns
                    </h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li><strong className="text-foreground">1. Duplicidade de cadastro</strong> — mesmo lead vira 3 oportunidades no CRM, time vende duas vezes para a mesma pessoa.</li>
                      <li><strong className="text-foreground">2. Campo obrigatório esquecido</strong> — entrega fica travada, suporte precisa correr atrás.</li>
                      <li><strong className="text-foreground">3. Cópia errada de e-mail</strong> — comunicação importante nunca chega, cliente acha que foi ignorado.</li>
                      <li><strong className="text-foreground">4. Cálculo manual de proposta</strong> — desconto aplicado errado, margem evapora silenciosamente.</li>
                    </ul>
                  </div>
                </section>

                {/* Chapter 7 */}
                <section className="mb-16" aria-labelledby="chapter-7">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10" aria-hidden="true">
                      <Zap className="h-5 w-5 text-primary" />
                    </div>
                    <h2 id="chapter-7" className="text-2xl font-bold md:text-3xl">
                      7. O Custo da Concorrência Que Já Automatizou
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Há um custo invisível ainda mais sutil: o custo competitivo. Enquanto sua empresa preenche
                    planilhas, o concorrente do bairro ao lado — ou do outro lado do país — fechou contrato com
                    três clientes que estavam te avaliando, simplesmente porque entregou primeiro um diagnóstico
                    bonito, gerou um PDF personalizado em 30 segundos e marcou a reunião automaticamente.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Em 2026, a percepção de profissionalismo está atrelada à velocidade e à personalização. Um
                    cliente que recebe um PDF de diagnóstico com o nome dele, dados específicos do negócio dele
                    e recomendações sob medida — tudo isso minutos depois de preencher um formulário — sente que
                    está diante de uma empresa "moderna, organizada, séria". Quem entrega isso em 5 dias por
                    e-mail soa como "amador, lento, do passado".
                  </p>

                  <figure className="mb-6">
                    <img
                      src={imgRobo}
                      alt="Mão humana e mão robótica se aproximando, simbolizando colaboração entre pessoas e inteligência artificial na automação empresarial"
                      className="rounded-xl w-full object-cover"
                      loading="lazy"
                    />
                  </figure>
                </section>

                {/* Chapter 8 — Where to start */}
                <section className="mb-16" aria-labelledby="chapter-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10" aria-hidden="true">
                      <TrendingUp className="h-5 w-5 text-accent" />
                    </div>
                    <h2 id="chapter-8" className="text-2xl font-bold md:text-3xl">
                      8. Por Onde Começar a Automatizar (Sem Gastar Fortunas)
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Boa notícia: você não precisa de um projeto de transformação digital de 12 meses para
                    começar a parar de pagar o imposto do manual. A regra prática é começar pelo ponto onde a
                    dor é maior e o ROI é mais rápido. E quase sempre esse ponto é a <strong className="text-foreground">
                    porta de entrada do lead</strong>.
                  </p>

                  <div className="grid gap-4 md:grid-cols-2 mb-8">
                    <Card className="border-primary/30">
                      <CardContent className="p-6">
                        <span className="text-xs font-semibold text-primary">PASSO 1</span>
                        <h3 className="font-bold mt-1 mb-2">Automatize a qualificação de leads</h3>
                        <p className="text-sm text-muted-foreground">Formulário inteligente com IA, que adapta as perguntas conforme as respostas e descarta lead fora do perfil antes de chegar no time.</p>
                      </CardContent>
                    </Card>
                    <Card className="border-primary/30">
                      <CardContent className="p-6">
                        <span className="text-xs font-semibold text-primary">PASSO 2</span>
                        <h3 className="font-bold mt-1 mb-2">Automatize a geração de propostas/diagnósticos</h3>
                        <p className="text-sm text-muted-foreground">PDF criado pela IA no momento, com nome e contexto do lead, entregue por e-mail e WhatsApp instantaneamente.</p>
                      </CardContent>
                    </Card>
                    <Card className="border-accent/30">
                      <CardContent className="p-6">
                        <span className="text-xs font-semibold text-accent">PASSO 3</span>
                        <h3 className="font-bold mt-1 mb-2">Integre com seu CRM ou planilha</h3>
                        <p className="text-sm text-muted-foreground">Webhook, Zapier ou Make: o dado nunca mais é digitado duas vezes. Acaba a duplicidade.</p>
                      </CardContent>
                    </Card>
                    <Card className="border-accent/30">
                      <CardContent className="p-6">
                        <span className="text-xs font-semibold text-accent">PASSO 4</span>
                        <h3 className="font-bold mt-1 mb-2">Mensure o tempo devolvido</h3>
                        <p className="text-sm text-muted-foreground">Recalcule a fórmula do capítulo 2 em 30 dias. O número que sobe é seu novo lucro.</p>
                      </CardContent>
                    </Card>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    O ponto crítico aqui é começar pequeno e medir. Quem tenta automatizar tudo de uma vez se
                    afoga em integração e desiste. Quem automatiza apenas a porta de entrada — formulário +
                    IA + PDF + integração simples — sente o ROI no primeiro mês e ganha confiança para avançar.
                  </p>
                </section>

                {/* Chapter 9 — FAQ-style */}
                <section className="mb-16" aria-labelledby="chapter-9">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10" aria-hidden="true">
                      <Lightbulb className="h-5 w-5 text-primary" />
                    </div>
                    <h2 id="chapter-9" className="text-2xl font-bold md:text-3xl">
                      9. Objeções Comuns — e Por Que Elas Custam Caro
                    </h2>
                  </div>

                  <div className="space-y-4 mb-6">
                    <details className="rounded-xl border border-border bg-card/50 p-5 group">
                      <summary className="font-semibold cursor-pointer flex items-center justify-between">
                        <span>"Meu negócio é muito específico, automação não atende"</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden="true" />
                      </summary>
                      <p className="text-sm text-muted-foreground mt-3">
                        Em 2026, ferramentas com IA aprendem o seu contexto a partir de exemplos. O que era "específico
                        demais" há 3 anos é configurável em 20 minutos hoje. A objeção real costuma ser desconhecimento,
                        não inadequação.
                      </p>
                    </details>
                    <details className="rounded-xl border border-border bg-card/50 p-5 group">
                      <summary className="font-semibold cursor-pointer flex items-center justify-between">
                        <span>"Meus clientes preferem o atendimento humano"</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden="true" />
                      </summary>
                      <p className="text-sm text-muted-foreground mt-3">
                        Sim — para o que importa. Automação não substitui o atendimento humano nos momentos decisivos;
                        ela libera seu time da burocracia para que possa <em>finalmente</em> ser humano onde faz diferença.
                      </p>
                    </details>
                    <details className="rounded-xl border border-border bg-card/50 p-5 group">
                      <summary className="font-semibold cursor-pointer flex items-center justify-between">
                        <span>"Vou perder o controle se a IA decidir"</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden="true" />
                      </summary>
                      <p className="text-sm text-muted-foreground mt-3">
                        Automação bem feita é assistida: a IA propõe, você define as regras. Você ganha controle, não perde:
                        passa a ver dados que antes ficavam soltos em conversas de WhatsApp e cadernos.
                      </p>
                    </details>
                    <details className="rounded-xl border border-border bg-card/50 p-5 group">
                      <summary className="font-semibold cursor-pointer flex items-center justify-between">
                        <span>"Não tenho R$ 500/mês para investir agora"</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" aria-hidden="true" />
                      </summary>
                      <p className="text-sm text-muted-foreground mt-3">
                        Refaça a conta do capítulo 2. Se você está pagando R$ 8.000 por mês em custo invisível, R$ 500 para
                        economizar isso não é despesa — é investimento com ROI de 16x. E há planos gratuitos para começar.
                      </p>
                    </details>
                  </div>
                </section>

                {/* Chapter 10 — Conclusion */}
                <section className="mb-16" aria-labelledby="chapter-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10" aria-hidden="true">
                      <Rocket className="h-5 w-5 text-accent" />
                    </div>
                    <h2 id="chapter-10" className="text-2xl font-bold md:text-3xl">
                      10. O Verdadeiro Custo é Esperar
                    </h2>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Cada mês que sua empresa continua operando manualmente é um mês inteiro de salários,
                    energia, atenção e oportunidade jogados em tarefas que software já resolve melhor, mais
                    rápido e mais barato. Isso não é uma opinião filosófica sobre o futuro do trabalho — é
                    contabilidade simples.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Em 2026, automatizar deixou de ser vantagem competitiva: virou requisito de
                    sobrevivência. Empresas que automatizam a porta de entrada do lead, a qualificação por IA
                    e a geração de documentos personalizados ganham margem, ganham velocidade e ganham
                    profissionalismo aos olhos do cliente. Quem não automatiza paga, todo mês, um imposto
                    silencioso que ninguém cobra na nota — mas que aparece, sempre, no balanço do fim do ano.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-6">
                    A pergunta deste artigo, no fundo, não é "quanto custa não automatizar". É: <strong className="text-foreground">
                    quanto a mais você precisa perder para finalmente decidir começar?</strong> A boa notícia é
                    que começar é simples, barato e pode ser feito ainda hoje. A má notícia é que cada dia que
                    passa, você continua pagando — e seu concorrente continua coletando o que era seu.
                  </p>

                  <div className="rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 to-accent/10 p-6 md:p-8 text-center">
                    <Trophy className="mx-auto mb-3 h-10 w-10 text-primary" aria-hidden="true" />
                    <h3 className="text-xl font-bold mb-2">Sua decisão em 1 frase</h3>
                    <p className="text-muted-foreground">
                      Refaça hoje a fórmula do capítulo 2. Se o resultado for maior que R$ 300, você está
                      operando no prejuízo. Comece agora — gratuitamente — pelo passo 1.
                    </p>
                  </div>
                </section>

                {/* Final CTA */}
                <BlogCTA
                  title="Pronto Para Parar de Pagar o Imposto do Manual?"
                  description="Crie seu primeiro formulário inteligente com IA agora mesmo, gere PDFs automáticos para cada lead e descubra na prática quanto a automação devolve para sua operação."
                  buttonText="Começar Grátis Agora"
                  buttonLink="/auth?mode=signup"
                />

                {/* Related */}
                <div className="mt-16 pt-12 border-t border-border">
                  <h2 className="text-2xl font-bold mb-6">Continue Aprendendo</h2>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Link to="/blog/ia-para-qualificacao-de-leads" className="group rounded-xl border border-border p-5 hover:border-primary/50 transition-all">
                      <span className="text-xs text-primary font-medium">Inteligência Artificial</span>
                      <h3 className="font-semibold mt-1 group-hover:text-primary transition-colors">IA para Qualificação de Leads</h3>
                      <p className="text-sm text-muted-foreground mt-2">Como usar inteligência artificial para qualificar leads automaticamente.</p>
                    </Link>
                    <Link to="/blog/melhor-ferramenta-de-formulario-para-seu-negocio" className="group rounded-xl border border-border p-5 hover:border-primary/50 transition-all">
                      <span className="text-xs text-accent font-medium">Ferramentas & Comparativos</span>
                      <h3 className="font-semibold mt-1 group-hover:text-primary transition-colors">A Melhor Ferramenta de Formulário</h3>
                      <p className="text-sm text-muted-foreground mt-2">Comparativo completo das principais ferramentas do mercado.</p>
                    </Link>
                  </div>
                </div>

              </div>
            </div>
          </article>
        </main>

        <BlogFooter />
      </div>
    </>
  );
};

export default BlogPostCustoNaoAutomatizar;
